<template>
  <transition name="appointment">
    <div v-if="open" class="appointment-modal-container">
      <template v-if="!showChecklistForm">
        <!-- Sub-Modal: Item Selection List (Material, Order, Stop Reason, Rejection Reason) -->
        <SelectModal
          v-if="selectModal.open"
          translation-base="ProductionProgress.SelectModal."
          :title="selectModal.title"
          :selected-id="selectModal.selectedId"
          :list="selectModal.data"
          @select-item="handleSelectModalClick"
          @close="selectModal.open = false"
        />

        <!-- Sub-Modal: Touch Numeric Input Modal (Quantities, Tarugos, Lengths) -->
        <InputModal
          v-if="inputModal"
          :data="selectedFieldInputModal"
          @setQuantityValue="(context) => setValueInputModal(context)"
          @close="(title) => handleInputModal(title, 'close')"
        />

        <!-- Sub-Modal: Touch Text Input Modal (Alloy, Heat/Melt Number) -->
        <InputTextModal
          v-if="inputTextModal.open"
          :modal-name="inputTextModal.title"
          :input-value="inputTextModal.value"
          @setValue="handleInputTextModalSave"
          @close="inputTextModal.open = false"
        />

        <!-- Top Header Bar -->
        <AppointmentHeader
          :component-id="'extrusion-appointment-modal'"
          :back-label="headerBackLabel"
          :back-event="handleCloseModal"
          :orders="orderListDisplay"
          :status="headerStatus"
          :color="headerColor"
          :is-fixed="false"
          :orderObject="safeOrderObject"
          @removeAppointment="handleCloseModal"
        />

        <!-- Main Fields Container -->
        <div class="fields-container">
          <div class="fields-wrapper">
            <!-- Field Cards List -->
            <div class="fields-container-wrapper">
              <!-- Top OP Info Cards (Material & Ordem - 2 Columns) -->
              <div v-if="(data.material || data.order) && actionType !== 'FinishCutting'" class="top-op-cards-grid">
                <FieldCard
                  v-if="data.material"
                  :data="data.material"
                  @click.native="!data.material.disabled && openSelectModal('Material')"
                />
                <FieldCard
                  v-if="data.order"
                  :data="data.order"
                  @click.native="!data.order.disabled && openSelectModal('Order')"
                />
              </div>

              <!-- Unified Cutting Appointment (Baskets + Refuses with Real-Time Production Balance) -->
              <div v-if="actionType === 'Basket' || actionType === 'CuttingBasket' || actionType === 'CuttingUnified'" class="w-100 mb-2">
                <!-- Production Balance Summary Banner -->
                <div class="cutting-balance-summary mb-3" :class="{ 'cutting-balance-summary--exceeded': isCuttingExceeded, 'cutting-balance-summary--perfect': totalExtrusionBars > 0 && remainingBars === 0 }">
                  <div class="cutting-balance-summary__row">
                    <div class="cutting-balance-summary__item">
                      <span class="cutting-balance-summary__label">Produzido na Prensa:</span>
                      <span class="cutting-balance-summary__val">{{ totalExtrusionBars }} barras</span>
                    </div>
                    <div class="cutting-balance-summary__item">
                      <span class="cutting-balance-summary__label">Total em Cestos:</span>
                      <span class="cutting-balance-summary__val font-weight-bold text-primary">
                        {{ newBasketBars }} barras
                      </span>
                    </div>
                    <div class="cutting-balance-summary__item">
                      <span class="cutting-balance-summary__label">Total em Refugo:</span>
                      <span class="cutting-balance-summary__val font-weight-bold" :class="newRefuseBars > 0 ? 'text-warning' : 'text-muted'">
                        {{ newRefuseBars }} barras
                      </span>
                    </div>
                    <div class="cutting-balance-summary__item">
                      <span class="cutting-balance-summary__label">Total no Corte:</span>
                      <span class="cutting-balance-summary__val font-weight-bold">
                        {{ projectedCuttingTotalBars }} barras
                      </span>
                    </div>
                    <div class="cutting-balance-summary__item">
                      <span class="cutting-balance-summary__label">Saldo a Apontar:</span>
                      <span class="cutting-balance-summary__val font-weight-bold" :class="remainingBars < 0 ? 'text-danger' : (remainingBars === 0 && totalExtrusionBars > 0 ? 'text-success' : 'text-body')">
                        {{ remainingBars }} barras {{ remainingBars === 0 && totalExtrusionBars > 0 ? '✓' : '' }}
                      </span>
                    </div>
                  </div>

                  <div v-if="isCuttingExceeded" class="cutting-balance-summary__warning">
                    <WarnIcon />
                    <span>
                      Atenção: A soma dos cestos e refugos ({{ projectedCuttingTotalBars }} barras) ultrapassa a quantidade produzida na prensa ({{ totalExtrusionBars }} barras) em {{ Math.abs(remainingBars) }} barra(s).
                    </span>
                  </div>
                  <div v-else-if="totalExtrusionBars > 0 && remainingBars === 0" class="cutting-balance-summary__success">
                    <feather-icon icon="CheckCircleIcon" size="16" class="mr-50 text-success" />
                    <span class="text-success font-weight-bold">
                      Perfeito! O total de barras em cestos e refugos fecha exatamente com a produção da prensa ({{ totalExtrusionBars }} barras).
                    </span>
                  </div>
                </div>

                <!-- SECTION 1: CESTOS DE BARRAS -->
                <div class="cutting-section-block mb-3">
                  <div class="cutting-section-header">
                    <div class="cutting-section-title">
                      <div class="section-icon-box bg-purple-light mr-50">
                        <feather-icon icon="BoxIcon" size="16" class="text-purple" />
                      </div>
                      <span class="font-weight-bold font-raleway">Cestos de Barras</span>
                      <span class="badge badge-light-primary ml-50">{{ newBasketBars }} barras / {{ basketsList.length }} cesto(s)</span>
                    </div>
                    <button
                      type="button"
                      class="btn-add-section-item"
                      @click="addBasket"
                    >
                      <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                      <span>Adicionar Cesto</span>
                    </button>
                  </div>

                  <!-- Empty State when no baskets -->
                  <div v-if="basketsList.length === 0" class="empty-refuses-card text-center py-2 px-1 mb-2">
                    <p class="text-muted small mb-50">Nenhum cesto apontado nesta etapa (ex: refugo total da OP).</p>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      @click="addBasket"
                    >
                      <feather-icon icon="PlusIcon" size="12" class="mr-25" />
                      <span>Adicionar Cesto</span>
                    </button>
                  </div>

                  <!-- Baskets List -->
                  <div
                    v-for="(basket, index) in basketsList"
                    :key="basket.id || index"
                    class="refuse-entry mb-2"
                  >
                    <div class="refuse-entry__header">
                      <span class="refuse-entry__title">Cesto {{ index + 1 }}</span>
                      <button
                        type="button"
                        class="btn-delete-entry"
                        title="Excluir Cesto"
                        @click="deleteBasketAt(index)"
                      >
                        <feather-icon icon="Trash2Icon" size="14" class="text-danger" />
                      </button>
                    </div>

                    <div class="refuse-entry__fields">
                      <!-- Código do Cesto -->
                      <div
                        class="quantity-field-card"
                        :class="{ 'quantity-field-card--alert': !basket.basketCode }"
                      >
                        <div class="quantity-field-card__icon">
                          <OrderIcon />
                        </div>
                        <div class="quantity-field-card__content">
                          <span class="quantity-field-card__label">CÓDIGO DO CESTO</span>
                          <input
                            v-model="basket.basketCode"
                            type="text"
                            class="quantity-field-card__input"
                            placeholder="Informe o código do cesto"
                          />
                        </div>
                        <div v-if="!basket.basketCode" class="quantity-field-card__warn">
                          <WarnIcon />
                        </div>
                      </div>

                      <!-- Qtd. Barras -->
                      <div
                        class="quantity-field-card"
                        :class="{ 'quantity-field-card--alert': !basket.barQuantity || Number(basket.barQuantity) <= 0 }"
                      >
                        <div class="quantity-field-card__icon">
                          <ProducedIcon />
                        </div>
                        <div class="quantity-field-card__content">
                          <span class="quantity-field-card__label">QTD. BARRAS</span>
                          <input
                            v-model.number="basket.barQuantity"
                            type="number"
                            step="1"
                            inputmode="numeric"
                            class="quantity-field-card__input"
                            placeholder="0"
                          />
                        </div>
                        <div v-if="!basket.barQuantity || Number(basket.barQuantity) <= 0" class="quantity-field-card__warn">
                          <WarnIcon />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- SECTION 2: REFUGOS DO CORTE -->
                <div class="cutting-section-block mb-2">
                  <div class="cutting-section-header">
                    <div class="cutting-section-title">
                      <div class="section-icon-box bg-danger-light mr-50">
                        <feather-icon icon="Trash2Icon" size="16" class="text-danger" />
                      </div>
                      <span class="font-weight-bold font-raleway">Refugos do Corte</span>
                      <span v-if="refusesList.length > 0" class="badge badge-light-danger ml-50">{{ newRefuseBars }} barras / {{ refusesList.length }} motivo(s)</span>
                    </div>
                    <button
                      type="button"
                      class="btn-add-section-item btn-add-refuse"
                      @click="addRefuse"
                    >
                      <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                      <span>Adicionar Refugo</span>
                    </button>
                  </div>

                  <!-- Empty State when no refuses -->
                  <div v-if="refusesList.length === 0" class="empty-refuses-card text-center py-2 px-1">
                    <p class="text-muted small mb-50">Nenhum refugo apontado nesta etapa.</p>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="addRefuse"
                    >
                      <feather-icon icon="PlusIcon" size="12" class="mr-25" />
                      Apontar Refugo no Corte
                    </button>
                  </div>

                  <!-- Refuses List -->
                  <div
                    v-for="(refuse, index) in refusesList"
                    :key="refuse.id || index"
                    class="refuse-entry mb-2"
                  >
                    <div class="refuse-entry__header">
                      <span class="refuse-entry__title">Refugo {{ index + 1 }}</span>
                      <button
                        type="button"
                        class="btn-delete-entry"
                        title="Excluir Refugo"
                        @click="deleteRefuseAt(index)"
                      >
                        <feather-icon icon="Trash2Icon" size="14" class="text-danger" />
                      </button>
                    </div>

                    <div class="refuse-entry__fields">
                      <!-- Motivo de Refugo -->
                      <div
                        class="quantity-field-card cursor-pointer"
                        :class="{ 'quantity-field-card--alert': !refuse.reasonId }"
                        @click="openRefuseSelectModal(index)"
                      >
                        <div class="quantity-field-card__icon">
                          <ReasonIcon />
                        </div>
                        <div class="quantity-field-card__content">
                          <span class="quantity-field-card__label">MOTIVO DE REFUGO</span>
                          <input
                            :value="getReasonName(refuse.reasonId) || refuse.reasonName || 'Selecione o motivo'"
                            type="text"
                            readonly
                            class="quantity-field-card__input quantity-field-card__input--select cursor-pointer"
                            :class="{ 'quantity-field-card__input--placeholder': !refuse.reasonId }"
                          />
                        </div>
                        <div v-if="!refuse.reasonId" class="quantity-field-card__warn">
                          <WarnIcon />
                        </div>
                      </div>

                      <!-- Qtd. Barras / Barro Refugado -->
                      <div
                        class="quantity-field-card"
                        :class="{ 'quantity-field-card--alert': !refuse.quantityBars || Number(refuse.quantityBars) <= 0 }"
                      >
                        <div class="quantity-field-card__icon">
                          <ProducedIcon />
                        </div>
                        <div class="quantity-field-card__content">
                          <span class="quantity-field-card__label">QTD. BARRAS</span>
                          <input
                            v-model.number="refuse.quantityBars"
                            type="number"
                            step="1"
                            inputmode="numeric"
                            class="quantity-field-card__input"
                            placeholder="0"
                          />
                        </div>
                        <div v-if="!refuse.quantityBars || Number(refuse.quantityBars) <= 0" class="quantity-field-card__warn">
                          <WarnIcon />
                        </div>
                      </div>

                      <!-- Refugo (kg) (Opcional) -->
                      <div class="quantity-field-card">
                        <div class="quantity-field-card__icon">
                          <RejectionIcon />
                        </div>
                        <div class="quantity-field-card__content">
                          <span class="quantity-field-card__label">PESO (KG) - OPCIONAL</span>
                          <input
                            v-model.number="refuse.quantityKg"
                            type="number"
                            step="0.1"
                            inputmode="decimal"
                            class="quantity-field-card__input"
                            placeholder="0.0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Direct Inputs Grid for Partial Appointment (Tarugo Data) -->
              <div v-else-if="actionType === 'Partial'" class="partial-direct-inputs-grid">
                <!-- 1. Liga -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.alloy.value }">
                  <div class="quantity-field-card__icon">
                    <MaterialIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">LIGA</span>
                    <select
                      v-model="data.alloy.value"
                      class="quantity-field-card__input quantity-field-card__input--select cursor-pointer"
                      @change="onDirectFieldInput('alloy')"
                    >
                      <option value="" disabled>Selecione a Liga</option>
                      <option v-for="opt in computedAlloyOptions" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                  </div>
                  <div v-if="!data.alloy.value" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 2. Origem -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.origin.value }">
                  <div class="quantity-field-card__icon">
                    <ResourceIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">ORIGEM</span>
                    <select
                      v-model="data.origin.value"
                      class="quantity-field-card__input quantity-field-card__input--select cursor-pointer"
                      @change="onDirectFieldInput('origin')"
                    >
                      <option value="" disabled>Selecione a Origem</option>
                      <option value="Alumasa">Alumasa</option>
                      <option value="Importado">Importado</option>
                    </select>
                  </div>
                  <div v-if="!data.origin.value" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 3. Fusão / Corrida -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.heatNumber.value }">
                  <div class="quantity-field-card__icon">
                    <OrderIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">FUSÃO / CORRIDA</span>
                    <input
                      v-model="data.heatNumber.value"
                      type="text"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('heatNumber')"
                    />
                  </div>
                  <div v-if="!data.heatNumber.value" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 4. Tamanho Tarugo (mm) -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.sizeMm.value || Number(data.sizeMm.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">TAMANHO TARUGO (MM)</span>
                    <input
                      v-model.number="data.sizeMm.value"
                      type="number"
                      step="0.1"
                      inputmode="decimal"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('sizeMm')"
                    />
                  </div>
                  <div v-if="!data.sizeMm.value || Number(data.sizeMm.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 5. Qtd. Tarugos -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.quantity.value || Number(data.quantity.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">QTD. TARUGOS</span>
                    <input
                      v-model.number="data.quantity.value"
                      type="number"
                      step="1"
                      inputmode="numeric"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('quantity')"
                    />
                  </div>
                  <div v-if="!data.quantity.value || Number(data.quantity.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 6. Qtd. Barras -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.barQuantity.value || Number(data.barQuantity.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">QTD. BARRAS</span>
                    <input
                      v-model.number="data.barQuantity.value"
                      type="number"
                      step="1"
                      inputmode="numeric"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('barQuantity')"
                    />
                  </div>
                  <div v-if="!data.barQuantity.value || Number(data.barQuantity.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 6. Comprimento Puxada (m) -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.pullLengthM.value || Number(data.pullLengthM.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">COMPRIMENTO PUXADA (M)</span>
                    <input
                      v-model.number="data.pullLengthM.value"
                      type="number"
                      step="0.1"
                      inputmode="decimal"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('pullLengthM')"
                    />
                  </div>
                  <div v-if="!data.pullLengthM.value || Number(data.pullLengthM.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 7. Temp. Tarugo (°C) -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.temperature.value || Number(data.temperature.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">TEMP. TARUGO (°C)</span>
                    <input
                      v-model.number="data.temperature.value"
                      type="number"
                      step="1"
                      inputmode="numeric"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('temperature')"
                    />
                  </div>
                  <div v-if="!data.temperature.value || Number(data.temperature.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 8. Temp. Emergente (°C) -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.emergentTemperature.value || Number(data.emergentTemperature.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">TEMP. EMERGENTE (°C)</span>
                    <input
                      v-model.number="data.emergentTemperature.value"
                      type="number"
                      step="1"
                      inputmode="numeric"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('emergentTemperature')"
                    />
                  </div>
                  <div v-if="!data.emergentTemperature.value || Number(data.emergentTemperature.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 9. Vel. Extrusão (m/min) -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.extrusionSpeed.value || Number(data.extrusionSpeed.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">VEL. EXTRUSÃO (M/MIN)</span>
                    <input
                      v-model.number="data.extrusionSpeed.value"
                      type="number"
                      step="0.1"
                      inputmode="decimal"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('extrusionSpeed')"
                    />
                  </div>
                  <div v-if="!data.extrusionSpeed.value || Number(data.extrusionSpeed.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>

                <!-- 10. Peso Perfil (kg/m) -->
                <div class="quantity-field-card" :class="{ 'quantity-field-card--alert': !data.profileWeight.value || Number(data.profileWeight.value) <= 0 }">
                  <div class="quantity-field-card__icon">
                    <ProducedIcon />
                  </div>
                  <div class="quantity-field-card__content">
                    <span class="quantity-field-card__label">PESO PERFIL (KG/M)</span>
                    <input
                      v-model.number="data.profileWeight.value"
                      type="number"
                      step="0.001"
                      inputmode="decimal"
                      class="quantity-field-card__input"
                      @input="onDirectFieldInput('profileWeight')"
                    />
                  </div>
                  <div v-if="!data.profileWeight.value || Number(data.profileWeight.value) <= 0" class="quantity-field-card__warn">
                    <WarnIcon />
                  </div>
                </div>
              </div>

              <!-- Balance / Reconciliation View for Cutting Finalization (FinishCutting) -->
              <div v-else-if="actionType === 'FinishCutting'" class="w-100 mb-2">
                <div class="cutting-reconciliation-card mb-3">
                  <div class="cutting-reconciliation-card__header">
                    <span class="cutting-reconciliation-card__title">Balanço de Produção (Prensa vs Corte)</span>
                    <span class="cutting-reconciliation-card__badge" :class="reconciliationStatusClass">
                      {{ reconciliationBadgeText }}
                    </span>
                  </div>

                  <div class="cutting-reconciliation-card__metrics">
                    <div class="reconciliation-metric">
                      <span class="reconciliation-metric__label">Prensa (Extrusão)</span>
                      <span class="reconciliation-metric__value">{{ totalExtrusionBars }} barras</span>
                      <span class="reconciliation-metric__sub">{{ Number(totalExtrusionKg).toFixed(1) }} kg</span>
                    </div>

                    <div class="reconciliation-metric__divider">vs</div>

                    <div class="reconciliation-metric">
                      <span class="reconciliation-metric__label">Apontado no Corte</span>
                      <span class="reconciliation-metric__value">{{ currentCuttingTotalBars }} barras</span>
                      <span class="reconciliation-metric__sub">
                        {{ currentBasketBars }} cestos + {{ currentRefuseBars }} refugo ({{ Number(currentRefuseKg).toFixed(1) }} kg)
                      </span>
                    </div>

                    <div class="reconciliation-metric__divider">=</div>

                    <div class="reconciliation-metric">
                      <span class="reconciliation-metric__label">Diferença</span>
                      <span class="reconciliation-metric__value" :class="differenceValueClass">
                        {{ diffBarsPrefix }}{{ Math.abs(cuttingDiffBars) }} barras
                      </span>
                      <span class="reconciliation-metric__sub">{{ diffDescriptionText }}</span>
                    </div>
                  </div>

                  <!-- Alert Message based on difference -->
                  <div class="cutting-reconciliation-card__alert" :class="reconciliationAlertClass">
                    <div class="cutting-reconciliation-card__alert-icon">
                      <WarnIcon v-if="cuttingDiffBars !== 0" />
                      <span v-else class="check-icon">✓</span>
                    </div>
                    <div class="cutting-reconciliation-card__alert-content">
                      <span class="cutting-reconciliation-card__alert-title">{{ reconciliationAlertTitle }}</span>
                      <span class="cutting-reconciliation-card__alert-desc">{{ reconciliationAlertDesc }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Dedicated Direct Inputs for Extrusion Finalization (Finish) -->
              <div v-else-if="actionType === 'Finish'" class="w-100 mb-2">
                <!-- Validation Alert if no quantities -->
                <div v-if="totalExtrusionBars <= 0 && totalExtrusionKg <= 0" class="w-100 mb-2">
                  <div class="alert alert-danger font-weight-bold d-flex align-items-center mb-0" style="border-radius: 8px; font-size: 0.95rem;">
                    <feather-icon icon="AlertTriangleIcon" size="20" class="mr-1 flex-shrink-0" />
                    <span>Não é possível finalizar a extrusão sem realizar apontamentos de quantidade (barras produzidas).</span>
                  </div>
                </div>

                <div class="finish-direct-inputs-grid" :class="{ 'finish-direct-inputs-grid--3col': requiresRejectionReason }">
                  <!-- 1. Situação -->
                  <div class="quantity-field-card">
                    <div class="quantity-field-card__icon">
                      <StatusIcon />
                    </div>
                    <div class="quantity-field-card__content">
                      <span class="quantity-field-card__label">SITUAÇÃO</span>
                      <select
                        v-model="data.situation.value"
                        class="quantity-field-card__input quantity-field-card__input--select cursor-pointer"
                        @change="onSituationDirectChange"
                      >
                        <option disabled value="">Selecione</option>
                        <option value="Aprovado">Aprovado</option>
                        <option value="Aprovado parcial">Aprovado parcial</option>
                        <option value="Reprovado">Reprovado</option>
                        <option value="Teste">Teste</option>
                      </select>
                    </div>
                  </div>

                  <!-- 2. Motivo de Reprovação (exibido apenas quando Reprovado ou Aprovado parcial) -->
                  <div
                    v-if="requiresRejectionReason"
                    class="quantity-field-card"
                    :class="{ 'quantity-field-card--alert': !data.reason.value }"
                  >
                    <div class="quantity-field-card__icon">
                      <ReasonIcon />
                    </div>
                    <div class="quantity-field-card__content">
                      <span class="quantity-field-card__label">MOTIVO DE REPROVAÇÃO</span>
                      <select
                        v-model="data.reason.value"
                        class="quantity-field-card__input quantity-field-card__input--select cursor-pointer"
                        :class="{ 'quantity-field-card__input--placeholder': !data.reason.value }"
                        @change="onReasonDirectChange"
                      >
                        <option value="" disabled>Selecione o motivo...</option>
                        <option
                          v-for="r in computedExtrusionReasons"
                          :key="r"
                          :value="r"
                        >
                          {{ r }}
                        </option>
                      </select>
                    </div>
                    <div v-if="!data.reason.value" class="quantity-field-card__warn">
                      <WarnIcon />
                    </div>
                  </div>

                  <!-- 3. Observação -->
                  <div class="quantity-field-card">
                    <div class="quantity-field-card__icon">
                      <feather-icon icon="FileTextIcon" size="20" class="text-muted" />
                    </div>
                    <div class="quantity-field-card__content">
                      <span class="quantity-field-card__label">OBSERVAÇÃO</span>
                      <input
                        v-model="data.observation.value"
                        type="text"
                        placeholder="Observação (opcional)"
                        class="quantity-field-card__input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Dynamic Field Cards List for Other Types (Stop, etc.) -->
              <div v-else-if="activeFields.length > 0" class="w-100">
                <div class="fields-list">
                  <FieldCard
                    v-for="(fieldKey, index) in activeFields"
                    :key="`field-${fieldKey}`"
                    :class="[
                      (activeFields.length % 2 !== 0 && index === activeFields.length - 1)
                        ? 'grid-full'
                        : ''
                    ]"
                    v-if="data[fieldKey]"
                    :data="data[fieldKey]"
                    @click.native="handleFieldClick(fieldKey)"
                  />
                </div>
              </div>
            </div>

            <!-- Bottom Action Confirm Button -->
            <div class="apm__button-c">
              <Button :disabled="!canConfirm || submitting" @click="confirmAppointment">
                <span>{{ confirmButtonLabel }}</span>
              </Button>
            </div>
          </div>
        </div>
      </template>

      <!-- Checklist Form View (when applicable) -->
      <ChecklistForm
        v-else
        :checklist="checklistData"
        :show-cancel-button="false"
        :submission-error="checklistSubmissionError"
        @submit="handleChecklistSubmit"
        @finish="handleChecklistFinish"
      />
    </div>
  </transition>
</template>

<script>
import { BOverlay } from 'bootstrap-vue';
import LoadSpinner from '@/assets/images/pages/load-icon.svg';
import DateTimePicker from '@/components/components-code/date-time-picker/DatePickerFull.vue';
import AppointmentHeader from '@/views/pages/operator/components/AppointmentHeader';
import FieldCard from '@/views/pages/operator/components/AppointmentModal/components/FieldCard/FieldCard.vue';
import Button from '@/views/pages/operator/components/AppointmentModal/components/Button/Button.vue';
import InputModal from '@/views/pages/operator/components/InputModal/InputModal.vue';
import InputTextModal from '@/views/pages/operator/components/InputTextModal/InputTextModal.vue';
import SelectModal from '@/views/pages/operator/components/SelectModal/SelectModal.vue';
import ChecklistForm from '@/views/pages/operator/components/ChecklistForm/ChecklistForm.vue';
import WarnIcon from '@/assets/images/icons/warn-icon-1.svg';
import RejectionIcon from '@/assets/images/icons/refuse-icon-1.svg';
import ReasonIcon from '@/assets/images/icons/reason-icon-1.svg';
import ProducedIcon from '@/assets/images/icons/produced-icon-1.svg';
import OrderIcon from '@/assets/images/icons/order-details-icon.svg';
import ResourceIcon from '@/assets/images/icons/resource-icon-1.svg';
import MaterialIcon from '@core/assets/icons/material-icon.svg';
import StatusIcon from '@/assets/images/icons/status-icon-1.svg';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { createTarugoAppointment, saveCustomParameters, getCustomFieldTemplate, getCustomParameters, changeExtrusionStatus, finalizeExtrusion, getCuttingBaskets, saveCuttingBaskets, getCuttingRefuses, saveCuttingRefuses, finalizeCutting, getExtrusionHistory, getExtrusionAlloys } from '../extrusionApi';
import moment from 'moment';

export default {
  name: 'ExtrusionAppointmentModal',
  components: {
    BOverlay,
    LoadSpinner,
    DateTimePicker,
    AppointmentHeader,
    FieldCard,
    Button,
    InputModal,
    InputTextModal,
    SelectModal,
    ChecklistForm,
    WarnIcon,
    RejectionIcon,
    ReasonIcon,
    ProducedIcon,
    OrderIcon,
    ResourceIcon,
    MaterialIcon,
    StatusIcon,
    ToastificationContent
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    value: {
      type: Boolean,
      default: false
    },
    actionType: {
      type: String,
      default: 'Start' // Options: 'Start', 'Setup', 'Partial', 'Stop', 'Finish'
    },
    resource: {
      type: Object,
      default: () => ({})
    },
    siteId: {
      type: [Number, String],
      default: 1
    },
    orderObject: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      datepicker: new Date(),
      alloyOptions: [],
      tarugoForm: {
        alloy: '',
        heatNumber: '',
        origin: '',
        sizeMm: null,
        quantity: null,
        pullLengthM: null,
        temperature: null,
        emergentTemperature: null,
        extrusionSpeed: null,
        profileWeight: null
      },
      selectedTarugoFieldKey: null,
      inputTextModal: {
        open: false,
        title: '',
        value: ''
      },
      inputModal: false,
      selectedFieldInputModal: null,
      showChecklistForm: false,
      checklistData: null,
      checklistSubmissionError: null,
      selectModal: {
        open: false,
        title: '',
        selectedId: null,
        data: []
      },
      rawOrders: [],
      rawMaterials: [],
      rawReasons: [],
      selectedMaterial: null,
      selectedOrder: null,
      selectedReason: null,
      selectedStatus: null,
      contextStatusList: [],
      contextExhibitionLocations: [],
      contextRefuseReasons: [],
      contextPeopleList: [],
      dynamicFieldsList: [],
      basketsList: [
        {
          id: 1,
          basketCode: '',
          barQuantity: '',
          barSizeMm: ''
        }
      ],
      basketsCountInput: '1',
      selectedBasketIndex: null,
      selectedBasketFieldKey: null,
      refusesList: [
        {
          id: 1,
          quantityKg: '',
          reasonId: null
        }
      ],
      refusesCountInput: '1',
      selectedRefuseIndex: null,
      data: {
        material: {
          cardIcon: 'MaterialIcon',
          title: 'Material',
          value: '',
          alert: true,
          action: () => this.openSelectModal('Material')
        },
        order: {
          cardIcon: 'OrderIcon',
          title: 'Order',
          value: '',
          alert: true,
          action: () => this.openSelectModal('Order')
        },
        date: {
          cardIcon: 'DateIcon',
          title: 'Date',
          value: moment().format('DD/MM/YYYY HH:mm'),
          alert: false,
          disabled: false
        },
        status: {
          cardIcon: 'StatusIcon',
          title: 'Status',
          value: '',
          alert: false,
          disabled: false,
          action: () => this.openSelectModal('Status')
        },
        reason: {
          cardIcon: 'ReasonIcon',
          title: 'DisapprovalReason',
          value: '',
          alert: false,
          disabled: true,
          action: () => this.openSelectModal('DisapprovalReason')
        },
        produced: {
          cardIcon: 'ProducedIcon',
          title: 'Produced',
          value: 0,
          alert: false,
          disabled: false,
          action: () => this.openInputModal('Produced')
        },
        rejection: {
          cardIcon: 'RejectionIcon',
          title: 'Rejection',
          value: 0,
          alert: false,
          disabled: false,
          action: () => this.openInputModal('Rejection')
        },
        alloy: {
          cardIcon: 'MaterialIcon',
          title: 'Alloy',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('alloy')
        },
        heatNumber: {
          cardIcon: 'OrderIcon',
          title: 'HeatNumber',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('heatNumber')
        },
        origin: {
          cardIcon: 'ResourceIcon',
          title: 'Origin',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('origin')
        },
        sizeMm: {
          cardIcon: 'ProducedIcon',
          title: 'SizeMm',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('sizeMm')
        },
        quantity: {
          cardIcon: 'ProducedIcon',
          title: 'Quantity',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('quantity')
        },
        pullLengthM: {
          cardIcon: 'ProducedIcon',
          title: 'PullLengthM',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('pullLengthM')
        },
        temperature: {
          cardIcon: 'ProducedIcon',
          title: 'Temperature',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('temperature')
        },
        emergentTemperature: {
          cardIcon: 'ProducedIcon',
          title: 'EmergentTemperature',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('emergentTemperature')
        },
        extrusionSpeed: {
          cardIcon: 'ProducedIcon',
          title: 'ExtrusaoSpeed',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('extrusionSpeed')
        },
        profileWeight: {
          cardIcon: 'ProducedIcon',
          title: 'ProfileWeight',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.handleFieldClick('profileWeight')
        },
        situation: {
          cardIcon: 'StatusIcon',
          title: 'Situation',
          value: '',
          alert: false,
          disabled: false,
          action: () => this.openSelectModal('Situation')
        },
        observation: {
          cardIcon: 'ReasonIcon',
          title: 'Observation',
          value: '',
          alert: false,
          disabled: false,
          action: () => this.openTextInputModal('observation', 'Observação')
        },
        basketCode: {
          cardIcon: 'OrderIcon',
          title: 'BasketCode',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.openTextInputModal('basketCode', 'Código do Cesto')
        },
        barQuantity: {
          cardIcon: 'ProducedIcon',
          title: 'BarQuantity',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.openTarugoNumericModal('barQuantity', 'Qtd. de Barras')
        },
        barSizeMm: {
          cardIcon: 'ProducedIcon',
          title: 'BarSizeMm',
          value: '6000',
          alert: false,
          disabled: false,
          action: () => this.openTarugoNumericModal('barSizeMm', 'Tamanho da Barra (mm)')
        },
        rejectionKg: {
          cardIcon: 'RejectionIcon',
          title: 'RejectionKg',
          value: '',
          alert: true,
          disabled: false,
          action: () => this.openTarugoNumericModal('rejectionKg', 'Peso Refugado (kg)')
        },
        rejectionBars: {
          cardIcon: 'ProducedIcon',
          title: 'RejectionBars',
          value: '',
          alert: false,
          disabled: false,
          action: () => this.openTarugoNumericModal('rejectionBars', 'Qtd. Barras Refugadas')
        }
      }
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.open || this.value;
      },
      set(val) {
        this.$emit('update:open', val);
        this.$emit('input', val);
      }
    },
    headerBackLabel() {
      switch (this.actionType) {
        case 'Start': return 'Iniciar Apontamento';
        case 'Setup': return 'Iniciar Setup';
        case 'Partial': return 'Apontamento Parcial';
        case 'Stop': return 'Solicitar Parada';
        case 'Finish': return 'Finalizar Extrusão';
        case 'Basket':
        case 'CuttingBasket':
        case 'CuttingUnified': return 'Apontamento do Corte';
        case 'CuttingRefuse': return 'Apontar Refugo do Corte';
        case 'FinishCutting': return 'Finalizar Ordem';
        default: return 'Realizar Apontamento';
      }
    },
    confirmButtonLabel() {
      switch (this.actionType) {
        case 'Start': return 'Realizar Apontamento';
        case 'Setup': return 'Iniciar Setup';
        case 'Partial': return 'Salvar Apontamento';
        case 'Stop': return 'Confirmar Parada';
        case 'Finish': return 'Finalizar Extrusão';
        case 'Basket':
        case 'CuttingBasket':
        case 'CuttingUnified': return 'Salvar Apontamento do Corte';
        case 'CuttingRefuse': return this.refusesList.length > 1 ? `Salvar ${this.refusesList.length} Refugos` : 'Salvar Refugo';
        case 'FinishCutting': return 'Finalizar Ordem';
        default: return 'Confirmar';
      }
    },
    isUnstarted() {
      if (!this.orderObject || typeof this.orderObject !== 'object') return true;
      const code = this.orderObject.activeOrderNumber || this.orderObject.orderCode || this.orderObject.orderNumber || this.orderObject.code;
      if (!code || code === 'unstarted' || code === '-') return true;
      return false;
    },
    currentMatchedStatus() {
      if (this.actionType === 'Basket' || this.actionType === 'CuttingBasket' || this.actionType === 'CuttingUnified' || this.actionType === 'FinishCutting' || this.actionType === 'CuttingRefuse' || this.orderObject?.currentStep === 'Corte') {
        return { name: 'Corte', color: '#7367f0' };
      }
      if (this.isUnstarted) {
        return { name: 'Não Iniciado', color: '#808080' };
      }
      const sid = this.orderObject?.currentStatusId || this.orderObject?.statusId;
      if (sid === 4 || sid === 3 || this.actionType === 'Partial') {
        return { name: 'Executando', color: '#28c76f' };
      }
      if (sid === 2 || this.actionType === 'Setup') {
        return { name: 'Setup', color: '#7367f0' };
      }
      const name = this.orderObject?.currentStatusName || this.orderObject?.currentStep || this.orderObject?.lastStatus || 'Executando';
      const color = this.orderObject?.currentStatusColor || this.orderObject?.lastColorStatus || '#28c76f';
      return { name, color };
    },
    headerStatus() {
      return this.currentMatchedStatus.name;
    },
    headerColor() {
      return this.currentMatchedStatus.color;
    },
    orderListDisplay() {
      if (!this.isUnstarted && this.orderObject) {
        const code = this.orderObject.activeOrderNumber || this.orderObject.orderCode || this.orderObject.orderNumber || '';
        const mat = this.orderObject.materialName || this.orderObject.material || '';
        if (code) {
          return [{ name: `${code}${mat ? ' / ' + mat : ''}` }];
        }
      }
      if (this.selectedOrder) {
        return [{ name: `#${this.selectedOrder.orderCode} - ${this.selectedMaterial ? this.selectedMaterial.name : ''}` }];
      }
      if (this.resource && (this.resource.code || this.resource.name)) {
        return [{ name: this.resource.code || this.resource.name }];
      }
      return [{ name: 'Extrusão' }];
    },
    safeOrderObject() {
      if (this.orderObject && typeof this.orderObject === 'object') {
        return {
          producedQuantity: 0,
          plannedQuantity: 0,
          ...this.orderObject
        };
      }
      return { producedQuantity: 0, plannedQuantity: 0 };
    },
    computedAlloyOptions() {
      const base = (this.orderObject?.availableAlloys && Array.isArray(this.orderObject.availableAlloys) && this.orderObject.availableAlloys.length > 0)
        ? this.orderObject.availableAlloys
        : (this.alloyOptions || []);
      const list = [...base];

      const ordAlloy = this.orderObject?.alloy || this.orderObject?.productParameters?.liga;
      if (ordAlloy && ordAlloy !== '-' && !list.includes(ordAlloy)) {
        list.unshift(ordAlloy);
      }
      if (this.data.alloy?.value && !list.includes(this.data.alloy.value)) {
        list.unshift(this.data.alloy.value);
      }
      return Array.from(new Set(list.filter(x => x && x !== '-')));
    },
    // Production reconciliation and metrics for Cutting vs Extrusion
    totalExtrusionBars() {
      return Number(this.orderObject?.producedBars || this.orderObject?.totalProducedBars || 0);
    },
    totalExtrusionKg() {
      return Number(this.orderObject?.producedKg || this.orderObject?.totalProducedKg || 0);
    },
    currentBasketBars() {
      return Number(this.orderObject?.totalBasketBars || 0);
    },
    currentRefuseBars() {
      return Number(this.orderObject?.totalCuttingRefuseBars || 0);
    },
    currentRefuseKg() {
      return Number(this.orderObject?.totalCuttingRefuseKg || 0);
    },
    currentCuttingTotalBars() {
      return this.currentBasketBars + this.currentRefuseBars;
    },
    newBasketBars() {
      if (!Array.isArray(this.basketsList)) return 0;
      return this.basketsList.reduce((acc, b) => acc + (Number(b.barQuantity) || 0), 0);
    },
    newRefuseBars() {
      if (!Array.isArray(this.refusesList)) return 0;
      return this.refusesList.reduce((acc, r) => acc + (Number(r.quantityBars) || 0), 0);
    },
    projectedCuttingTotalBars() {
      if (this.actionType === 'Basket' || this.actionType === 'CuttingBasket' || this.actionType === 'CuttingUnified') {
        return this.newBasketBars + this.newRefuseBars;
      }
      if (this.actionType === 'CuttingRefuse') {
        return this.currentCuttingTotalBars + this.newRefuseBars;
      }
      return this.currentCuttingTotalBars;
    },
    remainingBars() {
      return this.totalExtrusionBars - this.projectedCuttingTotalBars;
    },
    isCuttingExceeded() {
      return this.totalExtrusionBars > 0 && this.projectedCuttingTotalBars > this.totalExtrusionBars;
    },
    cuttingDiffBars() {
      return this.totalExtrusionBars - this.currentCuttingTotalBars;
    },
    diffBarsPrefix() {
      if (this.cuttingDiffBars > 0) return '-';
      if (this.cuttingDiffBars < 0) return '+';
      return '';
    },
    diffDescriptionText() {
      if (this.totalExtrusionBars === 0) return 'Sem produção';
      if (this.cuttingDiffBars === 0) return '100% Conciliado';
      if (this.cuttingDiffBars > 0) return 'Faltam no corte';
      return 'Excedente na prensa';
    },
    reconciliationStatusClass() {
      if (this.totalExtrusionBars === 0) return 'badge-danger';
      if (this.currentCuttingTotalBars === 0) return 'badge-danger';
      if (this.cuttingDiffBars === 0) return 'badge-success';
      if (this.cuttingDiffBars > 0) return 'badge-danger';
      return 'badge-danger';
    },
    reconciliationBadgeText() {
      if (this.totalExtrusionBars === 0) return 'Bloqueado: Sem Produção na Prensa';
      if (this.currentCuttingTotalBars === 0) return 'Bloqueado: Sem Apontamentos no Corte';
      if (this.cuttingDiffBars === 0) return '100% Conciliado';
      if (this.cuttingDiffBars > 0) return `Bloqueado: Faltam ${this.cuttingDiffBars} barras`;
      return `Divergência: +${Math.abs(this.cuttingDiffBars)} barras excedentes`;
    },
    differenceValueClass() {
      if (this.totalExtrusionBars === 0) return 'text-danger';
      if (this.currentCuttingTotalBars === 0) return 'text-danger';
      if (this.cuttingDiffBars === 0) return 'text-success';
      if (this.cuttingDiffBars > 0) return 'text-danger';
      return 'text-danger';
    },
    reconciliationAlertClass() {
      if (this.totalExtrusionBars === 0) return 'alert-danger';
      if (this.currentCuttingTotalBars === 0) return 'alert-danger';
      if (this.cuttingDiffBars === 0) return 'alert-success';
      if (this.cuttingDiffBars > 0) return 'alert-danger';
      return 'alert-danger';
    },
    reconciliationAlertTitle() {
      if (this.totalExtrusionBars === 0) return 'Finalização Bloqueada: Sem Produção na Prensa';
      if (this.currentCuttingTotalBars === 0) return 'Finalização Bloqueada: Sem Apontamentos no Corte';
      if (this.cuttingDiffBars === 0) return 'Balanço de Produção Conferido (100% Conciliado)';
      if (this.cuttingDiffBars > 0) return 'Finalização Bloqueada: Faltam Barras no Corte';
      return 'Atenção: Quantidade Apontada no Corte Excede a Produção da Prensa';
    },
    reconciliationAlertDesc() {
      if (this.totalExtrusionBars === 0) {
        return 'Nenhum tarugo/barra extrudada foi apontado na prensa para esta ordem. Não é possível finalizar sem produção apontada.';
      }
      if (this.currentCuttingTotalBars === 0) {
        return `A prensa produziu ${this.totalExtrusionBars} barras, mas nenhum cesto ou refugo foi apontado no corte. Realize os apontamentos de cestos ou refugos antes de finalizar.`;
      }
      if (this.cuttingDiffBars === 0) {
        return `A soma das barras em cestos (${this.currentBasketBars}) e dos refugos (${this.currentRefuseBars}) fecha exatamente com a quantidade produzida na prensa (${this.totalExtrusionBars} barras). Balanço 100% aprovado para finalização.`;
      }
      if (this.cuttingDiffBars > 0) {
        return `Foram apontadas ${this.currentCuttingTotalBars} barras no corte (${this.currentBasketBars} em cestos + ${this.currentRefuseBars} refugadas), enquanto a prensa produziu ${this.totalExtrusionBars} barras. Faltam apontar ${this.cuttingDiffBars} barra(s) no corte (em cestos ou refugos) para fechar a produção. Não é possível finalizar enquanto houver peças pendentes.`;
      }
      return `Foram apontadas ${this.currentCuttingTotalBars} barras no corte (${this.currentBasketBars} em cestos + ${this.currentRefuseBars} refugadas), ultrapassando a quantidade produzida na prensa (${this.totalExtrusionBars} barras) em ${Math.abs(this.cuttingDiffBars)} barra(s).`;
    },
    activeFields() {
      switch (this.actionType) {
        case 'Start':
          return [];
        case 'Setup':
          return [];
        case 'Partial':
          return [
            'alloy',
            'origin',
            'heatNumber',
            'sizeMm',
            'quantity',
            'barQuantity',
            'pullLengthM',
            'temperature',
            'emergentTemperature',
            'extrusionSpeed',
            'profileWeight'
          ];
        case 'Stop':
          return ['status'];
        case 'Finish':
          return [
            'situation',
            ...( (this.data.situation?.value === 'Reprovado' || this.data.situation?.value === 'Aprovado parcial') ? ['reason'] : [] ),
            'observation'
          ];
        case 'Basket':
        case 'CuttingBasket':
          return ['basketCode', 'barQuantity'];
        case 'CuttingRefuse':
          return ['rejectionKg', 'rejectionBars', 'reason', 'observation'];
        case 'FinishCutting':
          return [];
        default:
          return [];
      }
    },
    canConfirm() {
      if (this.actionType === 'Start' || this.actionType === 'Setup') {
        return !!this.selectedOrder;
      }
      if (this.actionType === 'Partial') {
        const requiredKeys = [
          'alloy', 'origin', 'heatNumber', 'barQuantity', 'sizeMm', 'quantity', 'pullLengthM',
          'temperature', 'emergentTemperature', 'extrusionSpeed', 'profileWeight'
        ];
        return requiredKeys.every(k => {
          const val = this.data[k]?.value;
          if (typeof val === 'number') return val > 0;
          return !!val;
        });
      }
      if (this.actionType === 'Stop') {
        const hasStatusOrReason = !!this.selectedStatus || !!this.selectedReason;
        if (this.isUnstarted) {
          return !!this.selectedOrder && hasStatusOrReason;
        }
        return hasStatusOrReason;
      }
      if (this.actionType === 'Finish') {
        if (this.totalExtrusionBars <= 0 && this.totalExtrusionKg <= 0) {
          return false;
        }
        const sit = this.data.situation?.value;
        if (!sit) return false;
        if (sit === 'Reprovado' || sit === 'Aprovado parcial') {
          return !!this.selectedReason || !!this.data.reason?.value;
        }
        return true;
      }
      if (this.actionType === 'Basket' || this.actionType === 'CuttingBasket' || this.actionType === 'CuttingUnified') {
        const rawBaskets = Array.isArray(this.basketsList) ? this.basketsList : [];
        const rawRefuses = Array.isArray(this.refusesList) ? this.refusesList : [];

        // Cestos onde o operador inseriu dados (código ou quantidade > 0)
        const activeBaskets = rawBaskets.filter(b => !!String(b.basketCode || '').trim() || Number(b.barQuantity) > 0);
        // Refugos onde o operador informou quantidade ou motivo
        const activeRefuses = rawRefuses.filter(r => (Number(r.quantityBars) > 0 || Number(r.quantityKg) > 0) || !!(r.reasonId || r.refuseReasonId));

        // Deve haver pelo menos 1 cesto válido OU pelo menos 1 refugo válido
        const hasValidBaskets = activeBaskets.some(b => !!String(b.basketCode || '').trim() && Number(b.barQuantity) > 0);
        const hasValidRefuses = activeRefuses.some(r => (Number(r.quantityBars) > 0 || Number(r.quantityKg) > 0) && !!(r.reasonId || r.refuseReasonId));

        if (!hasValidBaskets && !hasValidRefuses) return false;

        // Se o operador começou a preencher cestos, todos os cestos ativos devem ter código e quantidade > 0
        if (activeBaskets.length > 0) {
          const basketsValid = activeBaskets.every(b => !!String(b.basketCode || '').trim() && Number(b.barQuantity) > 0);
          if (!basketsValid) return false;
        }

        // Se o operador adicionou refugos, todos os refugos ativos devem ter motivo e quantidade válida
        if (activeRefuses.length > 0) {
          const refusesValid = activeRefuses.every(r => (Number(r.quantityBars) > 0 || Number(r.quantityKg) > 0) && !!(r.reasonId || r.refuseReasonId));
          if (!refusesValid) return false;
        }

        // Não pode ultrapassar a quantidade produzida na prensa
        if (this.isCuttingExceeded) return false;

        // Deve haver pelo menos alguma quantidade apontada (barras em cesto/refugo ou peso em kg)
        const totalBars = this.newBasketBars + this.newRefuseBars;
        const totalKg = activeRefuses.reduce((acc, r) => acc + (Number(r.quantityKg) || 0), 0);
        if (totalBars <= 0 && totalKg <= 0) return false;

        return true;
      }
      if (this.actionType === 'FinishCutting') {
        if (this.totalExtrusionBars <= 0) {
          return false;
        }
        if (this.currentCuttingTotalBars <= 0) {
          return false;
        }
        if (this.currentCuttingTotalBars < this.totalExtrusionBars) {
          return false;
        }
        return true;
      }
      return true;
    },
    requiresRejectionReason() {
      const sit = this.data.situation?.value;
      return sit === 'Reprovado' || sit === 'Aprovado parcial';
    },
    computedExtrusionReasons() {
      const defaultReasons = [
        'Acabamento',
        'Arrancamento',
        'Chapéu',
        'Corrida',
        'Dimensional',
        'Entupiu',
        'Esquadro',
        'Forçando',
        'Fria',
        'Ondulando',
        'Peso',
        'Planicidade',
        'Quebrou',
        'Rasgou',
        'Relevo',
        'Risco',
        'Sujeira',
        'Teste',
        'Torcendo'
      ];
      const list = [...defaultReasons];
      if (Array.isArray(this.rawReasons) && this.rawReasons.length > 0) {
        this.rawReasons.forEach(r => {
          const name = r.name || r.description || '';
          if (name && !list.includes(name)) {
            list.push(name);
          }
        });
      }
      return list.sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }));
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(val) {
        if (val) {
          this.initModalData();
        }
      }
    }
  },
  methods: {
    async fetchContextData() {
      if (!this.siteId) return;
      try {
        const response = await this.$http2.get(`/api/mysfc/operation-progress/context?siteId=${this.siteId}&operationId=${this.orderObject?.operationId || ""}&resourceId=${this.resource?.resourceId || this.resource?.id || ""}`);
        if (response.data && response.data.data) {
          const data = response.data.data;
          this.contextStatusList = data.status || [];
          this.contextExhibitionLocations = data.exhibitionLocations || [];
          if (data.refuseReasons && Array.isArray(data.refuseReasons)) {
            this.contextRefuseReasons = data.refuseReasons;
            this.rawReasons = data.refuseReasons.map(r => ({ id: r.id, name: r.name || r.description }));
          }
          this.contextPeopleList = data.person || [];
        }
      } catch (err) {
        console.error('Erro ao carregar contexto de operação:', err);
      }
    },
    async initModalData() {
      this.loading = true;
      this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', true);

      try {
        this.datepicker = new Date();
        this.data.date.value = moment().format('DD/MM/YYYY HH:mm:ss');
        this.showChecklistForm = false;
        this.selectedReason = null;
        this.selectedStatus = null;

        await this.fetchContextData();

        const hasActiveOp = !this.isUnstarted && this.orderObject && (this.orderObject.activeOrderNumber || this.orderObject.orderCode || this.orderObject.orderNumber);

        if (hasActiveOp) {
          const matName = this.orderObject.materialName || this.orderObject.material || '';
          const ordCode = this.orderObject.activeOrderNumber || this.orderObject.orderCode || this.orderObject.orderNumber || '';

          this.selectedMaterial = { id: this.orderObject.materialId || matName, name: matName };
          this.selectedOrder = { id: this.orderObject.activeOrderId || this.orderObject.orderId || ordCode, orderCode: ordCode, orderNumber: ordCode };

          this.data.material = {
            ...this.data.material,
            value: matName,
            alert: false,
            disabled: true
          };

          this.data.order = {
            ...this.data.order,
            value: ordCode ? `#${ordCode}` : '',
            alert: false,
            disabled: true
          };
        } else {
          this.selectedMaterial = null;
          this.selectedOrder = null;

          this.data.material = {
            ...this.data.material,
            value: '',
            alert: true,
            disabled: false
          };

          this.data.order = {
            ...this.data.order,
            value: '',
            alert: true,
            disabled: false
          };
        }

        this.data.status = {
          ...this.data.status,
          value: '',
          alert: true,
          disabled: false
        };
        this.data.reason = {
          ...this.data.reason,
          value: '',
          alert: true,
          disabled: false
        };

        if (this.actionType === 'Partial') {
          let prevTarugo = this.orderObject?.lastTarugoDetails;

          if (!prevTarugo && this.orderObject?.operationId) {
            try {
              const opId = this.orderObject.operationId;
              const resId = this.orderObject?.resourceId || this.resource?.resourceId;
              if (opId && resId) {
                const histRes = await getExtrusionHistory({ operationId: opId, resourceId: resId });
                const list = histRes.data?.data || histRes.data || [];
                const lastExt = list.find(h => h.extrusionDetails && !h.extrusionDetails.situation);
                if (lastExt && lastExt.extrusionDetails) {
                  prevTarugo = lastExt.extrusionDetails;
                }
              }
            } catch (e) {
              console.error('Erro ao buscar apontamento anterior:', e);
            }
          }

          if ((!this.orderObject?.availableAlloys || this.orderObject.availableAlloys.length === 0) && (!this.alloyOptions || this.alloyOptions.length === 0)) {
            try {
              const alloysRes = await getExtrusionAlloys();
              const alloys = alloysRes.data?.data || alloysRes.data || [];
              if (Array.isArray(alloys) && alloys.length > 0) {
                this.alloyOptions = alloys;
              }
            } catch (e) {
              console.error('Erro ao buscar ligas:', e);
            }
          }

          // 1. Qtd. Tarugos e Qtd. Barras: permanecem vazios para cada novo apontamento
          if (this.data.barQuantity) {
            this.data.barQuantity.value = '';
            this.data.barQuantity.alert = true;
          }
          if (this.data.quantity) {
            this.data.quantity.value = '';
            this.data.quantity.alert = true;
          }

          // 2. Fusão / Corrida: sempre vem em branco (ajustes do cliente)
          if (this.data.heatNumber) {
            this.data.heatNumber.value = '';
            this.data.heatNumber.alert = true;
          }

          // 3. Origem: sempre vem em branco (fora do bloco azul que repete)
          if (this.data.origin) {
            this.data.origin.value = '';
            this.data.origin.alert = true;
          }

          // 4. Tamanho tarugo (mm): repete do apontamento anterior dentro da mesma OP (campo em azul); vazio se for o primeiro apontamento
          if (this.data.sizeMm) {
            const prevSize = prevTarugo?.sizeMm || prevTarugo?.tamanhoMm || prevTarugo?.size || prevTarugo?.tamanhoTarugo;
            this.data.sizeMm.value = (prevSize && Number(prevSize) > 0) ? Number(prevSize) : '';
            this.data.sizeMm.alert = !this.data.sizeMm.value || Number(this.data.sizeMm.value) <= 0;
          }

          // 5. Liga: repete do apontamento anterior ou da ordem atual (campo em azul)
          if (this.data.alloy) {
            const prefAlloy = prevTarugo?.alloy || this.orderObject?.alloy || this.orderObject?.customParameters?.Alloy || this.orderObject?.productParameters?.liga || '6063';
            this.data.alloy.value = (prefAlloy && prefAlloy !== '-') ? prefAlloy : '6063';
            this.data.alloy.alert = !this.data.alloy.value;
          }

          // 6. Campos da puxada e temperaturas (campos em azul): repetem do apontamento anterior dentro da mesma OP
          // Comprimento da Puxada (m)
          const pullVal = prevTarugo?.pullLengthM || '';
          if (this.data.pullLengthM) {
            this.data.pullLengthM.value = (pullVal && Number(pullVal) > 0) ? Number(pullVal) : '';
            this.data.pullLengthM.alert = !this.data.pullLengthM.value || Number(this.data.pullLengthM.value) <= 0;
          }

          // Temperatura do Tarugo (°C)
          const tempVal = prevTarugo?.temperature || '';
          if (this.data.temperature) {
            this.data.temperature.value = (tempVal && Number(tempVal) > 0) ? Number(tempVal) : '';
            this.data.temperature.alert = !this.data.temperature.value || Number(this.data.temperature.value) <= 0;
          }

          // Temperatura Emergente (°C)
          const emergTempVal = prevTarugo?.emergentTemperature || '';
          if (this.data.emergentTemperature) {
            this.data.emergentTemperature.value = (emergTempVal && Number(emergTempVal) > 0) ? Number(emergTempVal) : '';
            this.data.emergentTemperature.alert = !this.data.emergentTemperature.value || Number(this.data.emergentTemperature.value) <= 0;
          }

          // Velocidade de Extrusão (m/min)
          const speedVal = prevTarugo?.extrusionSpeedMMin || prevTarugo?.extrusionSpeed || '';
          if (this.data.extrusionSpeed) {
            this.data.extrusionSpeed.value = (speedVal && Number(speedVal) > 0) ? Number(speedVal) : '';
            this.data.extrusionSpeed.alert = !this.data.extrusionSpeed.value || Number(this.data.extrusionSpeed.value) <= 0;
          }

          // Peso do Perfil (kg/m)
          const pWeightVal = prevTarugo?.profileWeightKgM || prevTarugo?.profileWeight || '';
          if (this.data.profileWeight) {
            this.data.profileWeight.value = (pWeightVal && Number(pWeightVal) > 0) ? Number(pWeightVal) : '';
            this.data.profileWeight.alert = !this.data.profileWeight.value || Number(this.data.profileWeight.value) <= 0;
          }
        }

        if (this.actionType === 'Finish') {
          if (this.data.situation) {
            this.data.situation.value = '';
            this.data.situation.alert = false;
          }
          if (this.data.observation) {
            this.data.observation.value = '';
            this.data.observation.alert = false;
          }
          if (this.data.reason) {
            this.data.reason.title = 'DisapprovalReason';
            this.data.reason.value = '';
            this.data.reason.disabled = true;
            this.data.reason.alert = false;
          }
          this.selectedReason = null;
        }

        if (this.actionType === 'Basket' || this.actionType === 'CuttingBasket' || this.actionType === 'CuttingUnified') {
          this.basketsList = [
            {
              id: Date.now(),
              basketCode: '',
              barQuantity: ''
            }
          ];
          this.basketsCountInput = '1';
          this.refusesList = [];
          this.refusesCountInput = '0';
          this.selectedRefuseIndex = null;

          const opId = this.orderObject?.operationId || this.orderObject?.id;
          const resId = this.resource?.resourceId || this.resource?.id || this.orderObject?.resourceId;
          if (opId && resId) {
            try {
              const [basketsResp, refusesResp] = await Promise.all([
                getCuttingBaskets({ operationId: opId, resourceId: resId }),
                getCuttingRefuses({ operationId: opId, resourceId: resId })
              ]);

              const bList = basketsResp.data?.data || basketsResp.data || [];
              if (Array.isArray(bList) && bList.length > 0) {
                this.basketsList = bList.map(b => ({
                  id: b.id,
                  basketCode: b.basketCode || '',
                  barQuantity: b.barQuantity || '',
                  barSizeMm: b.barSizeMm || null
                }));
                this.basketsCountInput = String(this.basketsList.length);
              }

              const rList = refusesResp.data?.data || refusesResp.data || [];
              if (Array.isArray(rList) && rList.length > 0) {
                this.refusesList = rList.map(r => ({
                  id: r.id,
                  reasonId: r.refuseReasonId,
                  reasonName: r.reasonName || ((this.rawReasons || []).find(x => x.id === r.refuseReasonId)?.name) || '',
                  quantityBars: r.quantityBars || '',
                  quantityKg: r.quantityKg || '',
                  observation: r.observation || ''
                }));
                this.refusesCountInput = String(this.refusesList.length);
              }

              if ((!bList || bList.length === 0) && Array.isArray(rList) && rList.length > 0) {
                this.basketsList = [];
                this.basketsCountInput = '0';
              }
            } catch (e) {
              console.error('Erro ao buscar cestos e refugos existentes do corte:', e);
            }
          }
        }

        if (this.actionType === 'FinishCutting') {
          if (this.data.situation) {
            this.data.situation.value = 'Aprovado';
            this.data.situation.alert = false;
          }
          if (this.data.observation) {
            this.data.observation.value = '';
            this.data.observation.alert = false;
          }
          if (this.data.reason) {
            this.data.reason.title = 'DisapprovalReason';
            this.data.reason.value = '';
            this.data.reason.disabled = true;
            this.data.reason.alert = false;
          }
          this.selectedReason = null;
        }

        if (this.isUnstarted) {
          await Promise.all([
            this.fetchMaterials(''),
            this.fetchOrders('')
          ]);
        }
      } finally {
        this.loading = false;
        this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
      }
    },
    async fetchMaterials(orderId = '') {
      if (!this.resource?.resourceId) return;
      try {
        const orderParam = orderId ? `&orderId=${orderId}` : '&orderId=';
        const res = await this.$http2.get(
          `/api/mysfc/operator/material/selectable-for-resource?resourceId=${this.resource.resourceId}&siteId=${this.siteId}${orderParam}`
        );
        let list = res.data?.data || res.data || [];
        if (Array.isArray(list)) {
          this.rawMaterials = list.map(m => ({
            id: m.materialId || m.id,
            name: m.materialName || m.name || m.text,
            text: m.materialName || m.name || m.text,
            materialId: m.materialId || m.id,
            materialName: m.materialName || m.name
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar materiais selecionáveis:', err);
      }
    },
    async fetchOrders(materialId = '') {
      if (!this.resource?.resourceId) return;
      try {
        const matParam = materialId ? `&materialId=${materialId}` : '&materialId=';
        const res = await this.$http2.get(
          `/api/mysfc/operator/order/selectable-for-resource?resourceId=${this.resource.resourceId}&siteId=${this.siteId}${matParam}`
        );
        let list = res.data?.data || res.data || [];
        if (Array.isArray(list)) {
          this.rawOrders = list.map(o => ({
            id: o.orderId || o.id,
            orderId: o.orderId || o.id,
            orderCode: o.orderNumber || o.orderCode || String(o.orderId || o.id),
            orderNumber: o.orderNumber || o.orderCode,
            materialId: o.materialId,
            materialName: o.materialName || o.operationName || '',
            name: `${o.orderNumber || o.orderCode} / ${o.operationName || o.materialName || ''}`,
            text: `${o.orderNumber || o.orderCode} / ${o.operationName || o.materialName || ''}`
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar ordens selecionáveis:', err);
      }
    },
    handleCloseModal() {
      this.isOpen = false;
      this.$emit('close');
    },
    async openSelectModal(title) {
      if (title === 'Material') {
        this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', true);
        try {
          await this.fetchMaterials(this.selectedOrder?.id || '');
          this.selectModal = {
            open: true,
            title: 'Material',
            selectedId: this.selectedMaterial?.id || null,
            data: this.rawMaterials.map(m => ({
              id: m.id || m.materialId,
              name: m.name || m.materialName || m.text
            }))
          };
        } finally {
          this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
        }
      } else if (title === 'Order' || title === 'Ordem') {
        this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', true);
        try {
          await this.fetchOrders(this.selectedMaterial?.id || '');
          this.selectModal = {
            open: true,
            title: 'Order',
            selectedId: this.selectedOrder?.id || null,
            data: this.rawOrders.map(o => ({
              id: o.id || o.orderId,
              name: o.name || `#${o.orderCode} / ${o.materialName}`,
              orderCode: o.orderCode || o.orderNumber,
              materialName: o.materialName,
              materialId: o.materialId
            }))
          };
        } finally {
          this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
        }
      } else if (title === 'Reason' || title === 'DisapprovalReason' || title === 'RejectionReason' || title === 'Motivo de Refugo' || title === 'Motivo de Reprovação') {
        const extrusionReasons = [
          'Acabamento',
          'Arrancamento',
          'Chapéu',
          'Corrida',
          'Dimensional',
          'Entupiu',
          'Esquadro',
          'Forçando',
          'Fria',
          'Ondulando',
          'Peso',
          'Planicidade',
          'Quebrou',
          'Rasgou',
          'Relevo',
          'Risco',
          'Sujeira',
          'Teste',
          'Torcendo'
        ].map(name => ({ id: name, name }));

        const listData = (this.actionType === 'Finish' || this.actionType === 'FinishCutting')
          ? extrusionReasons
          : (this.rawReasons.length > 0 ? this.rawReasons.map(r => ({ id: r.id, name: r.name || r.description })) : extrusionReasons);

        this.selectModal = {
          open: true,
          title: (this.actionType === 'Finish' || this.actionType === 'FinishCutting') ? 'DisapprovalReason' : 'Reason',
          selectedId: this.selectedReason?.id || this.selectedReason?.name || this.data.reason?.value || null,
          data: listData
        };
      } else if (title === 'Origin' || title === 'Origem') {
        this.selectModal = {
          open: true,
          title: 'Origin',
          selectedId: this.data.origin?.value || 'Nacional',
          data: [
            { id: 'Nacional', name: 'Nacional' },
            { id: 'Importado', name: 'Importado' },
            { id: 'Interno', name: 'Reciclado / Interno' }
          ]
        };
      } else if (title === 'Situation' || title === 'Situação') {
        this.selectModal = {
          open: true,
          title: 'Situation',
          selectedId: this.data.situation?.value || 'Aprovado',
          data: [
            { id: 'Aprovado', name: 'Aprovado' },
            { id: 'Aprovado parcial', name: 'Aprovado parcial' },
            { id: 'Reprovado', name: 'Reprovado' },
            { id: 'Teste', name: 'Teste' }
          ]
        };
      } else if (title === 'Status') {
        let statusList = this.contextStatusList || [];

        if (this.actionType === 'Stop') {
          statusList = statusList.filter(
            (e) =>
              ((e.status_classification_id === 1 || e.statusClassificationId === 1) && (e.name !== 'Setup' || (e.id === 2 || e.value === 2))) ||
              e.status_classification_id === 2 || e.statusClassificationId === 2 ||
              e.status_classification_id === 3 || e.statusClassificationId === 3
          );
        }

        statusList = [...statusList].sort((a, b) =>
          String(a.text ?? a.name ?? '').localeCompare(
            String(b.text ?? b.name ?? ''),
            'pt',
            { sensitivity: 'base', numeric: true }
          )
        );

        this.selectModal = {
          open: true,
          title: 'Status',
          selectedId: this.selectedStatus?.id || this.data.status?.id || null,
          data: statusList.map(s => ({
            id: s.id || s.value,
            name: s.name || s.text,
            color: s.color,
            status_classification_id: s.status_classification_id || s.statusClassificationId
          }))
        };
      }
    },
    async handleSelectModalClick(payload) {
      this.selectModal.open = false;
      if (!payload) return;

      const title = payload.title || this.selectModal.title || '';
      const item = payload.item || payload;
      if (!item) return;

      if (title === 'Material' || title.toLowerCase().includes('material')) {
        const selectedName = item.name || item.text || item.materialName || (typeof item === 'string' ? item : '');
        const matId = item.id || item.materialId || selectedName;
        this.selectedMaterial = { id: matId, name: selectedName };
        this.data.material = {
          ...this.data.material,
          value: selectedName,
          alert: !selectedName
        };
        await this.fetchOrders(matId);
        await this.fetchDynamicFields(matId);
      } else if (title === 'Ordem' || title === 'Order' || title.toLowerCase().includes('order') || title.toLowerCase().includes('ordem')) {
        const code = item.orderCode || item.orderNumber || (item.name ? item.name.split(' ')[0].replace('#', '') : String(item.id || ''));
        const ordObj = {
          id: item.id || item.orderId,
          orderCode: code,
          orderNumber: code,
          materialName: item.materialName || '',
          materialId: item.materialId
        };
        this.selectedOrder = ordObj;
        this.data.order = {
          ...this.data.order,
          value: `#${code}`,
          alert: !code
        };

        if (ordObj.materialName && (!this.selectedMaterial || !this.data.material.value)) {
          const matName = ordObj.materialName;
          const matId = ordObj.materialId || matName;
          this.selectedMaterial = { id: matId, name: matName };
          this.data.material = {
            ...this.data.material,
            value: matName,
            alert: false
          };
        }
        await this.fetchDynamicFields(this.selectedMaterial?.id || ordObj.materialId || '');
      } else if (title === 'Origin' || title === 'Origem') {
        const selectedVal = item.name || item.id || (typeof item === 'string' ? item : '');
        if (this.data.origin) {
          this.data.origin.value = selectedVal;
          this.data.origin.alert = !selectedVal;
        }
      } else if (title === 'Situation' || title === 'Situação' || title.toLowerCase().includes('situation')) {
        const selectedVal = item.name || item.id || (typeof item === 'string' ? item : 'Aprovado');
        if (this.data.situation) {
          this.data.situation.value = selectedVal;
          this.data.situation.alert = false;
        }
        const requiresReason = selectedVal === 'Reprovado' || selectedVal === 'Aprovado parcial';
        if (this.data.reason) {
          this.data.reason.disabled = !requiresReason;
          this.data.reason.alert = requiresReason && !this.selectedReason;
          if (!requiresReason) {
            this.selectedReason = null;
            this.data.reason.value = '';
          }
        }
      } else if (title === 'Reason' || title === 'DisapprovalReason' || title === 'RejectionReason' || title === 'Motivo de Parada' || title.toLowerCase().includes('reason') || title.toLowerCase().includes('refugo') || title.toLowerCase().includes('reprovação')) {
        if (this.selectedRefuseIndex !== null && this.refusesList[this.selectedRefuseIndex]) {
          this.refusesList[this.selectedRefuseIndex].reasonId = item.id || item.refuseReasonId;
          this.selectedRefuseIndex = null;
        } else {
          this.selectedReason = item;
          this.selectedStatus = item;
          this.data.reason = {
            ...this.data.reason,
            value: item.name || item.description || item.text || '',
            alert: false
          };
        }
      } else if (title === 'Status' || title.toLowerCase().includes('status')) {
        const selectedName = item.name || item.text || item.description || (typeof item === 'string' ? item : '');
        const statId = item.id || item.value || selectedName;
        this.selectedStatus = { id: statId, name: selectedName, color: item.color };
        this.data.status = {
          ...this.data.status,
          id: statId,
          value: selectedName,
          alert: !selectedName
        };
      }
    },
    openInputModal(fieldKey) {
      this.selectedTarugoFieldKey = null;
      this.selectedFieldInputModal = {
        title: fieldKey === 'Produced' ? 'Quantidade Produzida' : 'Quantidade Refugada',
        quantity: this.data[fieldKey.toLowerCase()]?.value || 0
      };
      this.inputModal = true;
    },
    openTarugoNumericModal(fieldKey, title) {
      this.selectedTarugoFieldKey = fieldKey;
      this.selectedFieldInputModal = {
        title: title,
        quantity: Number(this.data[fieldKey]?.value) || 0
      };
      this.inputModal = true;
    },
    openTextInputModal(fieldKey, title) {
      this.selectedTarugoFieldKey = fieldKey;
      this.inputTextModal = {
        open: true,
        title: title,
        value: String(this.data[fieldKey]?.value || '')
      };
    },
    addBasket() {
      this.basketsList.push({
        id: Date.now() + Math.random(),
        basketCode: '',
        barQuantity: ''
      });
      this.basketsCountInput = String(this.basketsList.length);
    },
    removeBasket() {
      if (this.basketsList.length > 0) {
        this.basketsList.pop();
        this.basketsCountInput = String(this.basketsList.length);
      }
    },
    deleteBasketAt(index) {
      this.basketsList.splice(index, 1);
      this.basketsCountInput = String(this.basketsList.length);
    },
    onBasketsCountInput(e) {
      this.basketsCountInput = e.target.value;
    },
    applyBasketsCountInput() {
      let val = parseInt(this.basketsCountInput, 10);
      if (isNaN(val) || val < 0) val = 0;
      if (val > 20) val = 20;
      this.basketsCountInput = String(val);

      while (this.basketsList.length < val) {
        this.basketsList.push({
          id: Date.now() + Math.random(),
          basketCode: '',
          barQuantity: ''
        });
      }
      while (this.basketsList.length > val) {
        this.basketsList.pop();
      }
    },
    addRefuse() {
      this.refusesList.push({
        id: Date.now() + Math.random(),
        quantityKg: '',
        quantityBars: '',
        reasonId: null
      });
      this.refusesCountInput = String(this.refusesList.length);
    },
    removeRefuse() {
      if (this.refusesList.length > 1) {
        this.refusesList.pop();
        this.refusesCountInput = String(this.refusesList.length);
      }
    },
    deleteRefuseAt(index) {
      this.refusesList.splice(index, 1);
      this.refusesCountInput = String(this.refusesList.length);
    },
    onRefusesCountInput(e) {
      this.refusesCountInput = e.target.value;
    },
    applyRefusesCountInput() {
      let val = parseInt(this.refusesCountInput, 10);
      if (isNaN(val) || val < 1) val = 1;
      if (val > 20) val = 20;
      this.refusesCountInput = String(val);

      while (this.refusesList.length < val) {
        this.refusesList.push({
          id: Date.now() + Math.random(),
          quantityKg: '',
          quantityBars: '',
          reasonId: null
        });
      }
      while (this.refusesList.length > val) {
        this.refusesList.pop();
      }
    },
    openRefuseNumericInput(index, fieldKey, title) {
      this.selectedRefuseIndex = index;
      this.selectedFieldInputModal = {
        title,
        quantity: Number(this.refusesList[index]?.[fieldKey]) || 0
      };
      this.inputModal = true;
    },
    openRefuseSelectModal(index) {
      this.selectedRefuseIndex = index;
      this.openSelectModal('Reason');
    },
    getReasonName(reasonId) {
      if (!reasonId) return '';
      const found = this.rawReasons.find(r => r.id === reasonId || r.refuseReasonId === reasonId);
      return found?.name || found?.description || found?.text || '';
    },
    onDirectFieldInput(fieldKey) {
      if (!this.data[fieldKey]) return;
      const val = this.data[fieldKey].value;
      if (typeof val === 'number') {
        this.data[fieldKey].alert = isNaN(val) || val <= 0;
      } else if (typeof val === 'string') {
        this.data[fieldKey].alert = !val.trim();
      } else {
        this.data[fieldKey].alert = !val;
      }
    },
    onSituationDirectChange() {
      const selectedVal = this.data.situation?.value || 'Aprovado';
      if (this.data.situation) {
        this.data.situation.alert = false;
      }
      const requiresReason = selectedVal === 'Reprovado' || selectedVal === 'Aprovado parcial';
      if (this.data.reason) {
        this.data.reason.disabled = !requiresReason;
        this.data.reason.alert = requiresReason && !this.data.reason.value;
        if (!requiresReason) {
          this.selectedReason = null;
          this.data.reason.value = '';
        }
      }
    },
    onReasonDirectChange() {
      const val = this.data.reason?.value;
      if (val) {
        this.selectedReason = { id: val, name: val };
        this.selectedStatus = this.selectedReason;
        if (this.data.reason) {
          this.data.reason.alert = false;
        }
      } else {
        this.selectedReason = null;
        if (this.data.reason) {
          this.data.reason.alert = true;
        }
      }
    },
    openBasketTextInput(bIdx, fieldKey, title) {
      this.selectedBasketIndex = bIdx;
      this.selectedBasketFieldKey = fieldKey;
      this.inputTextModal = {
        open: true,
        title,
        value: String(this.basketsList[bIdx]?.[fieldKey] || '')
      };
    },
    openBasketNumericInput(bIdx, fieldKey, title) {
      this.selectedBasketIndex = bIdx;
      this.selectedBasketFieldKey = fieldKey;
      this.selectedFieldInputModal = {
        title,
        quantity: Number(this.basketsList[bIdx]?.[fieldKey]) || 0
      };
      this.inputModal = true;
    },
    handleInputTextModalSave(val) {
      if (this.selectedBasketIndex !== null && this.selectedBasketFieldKey) {
        if (this.basketsList[this.selectedBasketIndex]) {
          this.basketsList[this.selectedBasketIndex][this.selectedBasketFieldKey] = val || '';
        }
        this.selectedBasketIndex = null;
        this.selectedBasketFieldKey = null;
      } else if (this.selectedTarugoFieldKey && this.data[this.selectedTarugoFieldKey]) {
        const key = this.selectedTarugoFieldKey;
        this.data[key].value = val || '';
        if (key !== 'observation') {
          this.data[key].alert = !val;
        }
        this.selectedTarugoFieldKey = null;
      }
      this.inputTextModal.open = false;
    },
    setValueInputModal(context) {
      const { title, quantity } = context;
      if (this.selectedRefuseIndex !== null) {
        if (this.refusesList[this.selectedRefuseIndex]) {
          this.refusesList[this.selectedRefuseIndex].quantityKg = quantity || '';
        }
        this.selectedRefuseIndex = null;
      } else if (this.selectedBasketIndex !== null && this.selectedBasketFieldKey) {
        if (this.basketsList[this.selectedBasketIndex]) {
          this.basketsList[this.selectedBasketIndex][this.selectedBasketFieldKey] = quantity || '';
        }
        this.selectedBasketIndex = null;
        this.selectedBasketFieldKey = null;
      } else if (this.selectedTarugoFieldKey && this.data[this.selectedTarugoFieldKey]) {
        const key = this.selectedTarugoFieldKey;
        this.data[key].value = quantity;
        this.data[key].alert = !quantity || quantity <= 0;
        this.selectedTarugoFieldKey = null;
      } else {
        const key = title.includes('Produzida') ? 'produced' : 'rejection';
        if (this.data[key]) {
          this.data[key].value = quantity;
        }
      }
      this.inputModal = false;
    },
    handleDatepickerChange(val) {
      this.data.date.value = moment(val).format('DD/MM/YYYY HH:mm');
    },
    handleFieldClick(fieldKey) {
      if (fieldKey === 'produced' || fieldKey === 'rejection') {
        this.openInputModal(fieldKey === 'produced' ? 'Produced' : 'Rejection');
      } else if (fieldKey === 'reason') {
        if (!this.data.reason?.disabled) {
          this.openSelectModal('Reason');
        }
      } else if (fieldKey === 'status') {
        this.openSelectModal('Status');
      } else if (fieldKey === 'origin') {
        this.openSelectModal('Origin');
      } else if (fieldKey === 'situation') {
        this.openSelectModal('Situation');
      } else if (fieldKey === 'observation') {
        this.openTextInputModal('observation', 'Observação');
      } else if (fieldKey === 'basketCode') {
        this.openTextInputModal('basketCode', 'Código do Cesto');
      } else if (fieldKey === 'barQuantity') {
        this.openTarugoNumericModal('barQuantity', 'Qtd. de Barras');
      } else if (fieldKey === 'barSizeMm') {
        this.openTarugoNumericModal('barSizeMm', 'Tamanho da Barra (mm)');
      } else if (fieldKey === 'rejectionKg') {
        this.openTarugoNumericModal('rejectionKg', 'Peso Refugado (kg)');
      } else if (fieldKey === 'rejectionBars') {
        this.openTarugoNumericModal('rejectionBars', 'Qtd. Barras Refugadas');
      } else if (fieldKey === 'alloy' || fieldKey === 'heatNumber') {
        const titles = { alloy: 'Liga', heatNumber: 'Fusão / Corrida' };
        this.openTextInputModal(fieldKey, titles[fieldKey]);
      } else if (this.data[fieldKey]) {
        const titles = {
          sizeMm: 'Tamanho tarugo (mm)',
          quantity: 'Qtd. Tarugos',
          pullLengthM: 'Comprimento Puxada (m)',
          temperature: 'Temp. Tarugo (°C)',
          emergentTemperature: 'Temp. Emergente (°C)',
          extrusionSpeed: 'Vel. Extrusão (m/min)',
          profileWeight: 'Peso Perfil (kg/m)'
        };
        this.openTarugoNumericModal(fieldKey, titles[fieldKey] || fieldKey);
      }
    },
    async fetchDynamicFields() {
      this.dynamicFieldsList = [];
    },
    handleDynamicFieldClick(dynField) {
      if (!dynField) return;
      this.selectedFieldInputModal = {
        title: dynField.label || dynField.fieldName,
        quantity: dynField.value || 0,
        dynField
      };
      this.inputModal = true;
    },
    async confirmAppointment() {
      if (this.submitting || (this.actionType === 'Finish' && !this.canConfirm)) return;
      this.submitting = true;
      this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', true);

      try {
        const orderCode = this.selectedOrder?.orderCode || this.selectedOrder?.orderNumber;
        const orderId = this.selectedOrder?.id || this.selectedOrder?.orderId;
        const resourceId = this.resource?.resourceId || this.resource?.id;

        // 1. Salvar os parâmetros customizados dinâmicos (caso tenham sido informados)
        if (Array.isArray(this.dynamicFieldsList) && this.dynamicFieldsList.length > 0) {
          const paramsMap = {};
          this.dynamicFieldsList.forEach(f => {
            if (f.fieldName) {
              paramsMap[f.fieldName] = f.value;
            }
          });

          const entityIdToSave = orderCode || (this.selectedMaterial?.id ? String(this.selectedMaterial.id) : '');
          if (entityIdToSave) {
            try {
              await saveCustomParameters({
                entityType: orderCode ? 'Order' : 'Material',
                entityId: entityIdToSave,
                parameters: paramsMap
              }, this.siteId);
            } catch (err) {
              console.warn('Aviso: Erro ao salvar parâmetros customizados:', err);
            }
          }
        }

        // 2. Realizar o Apontamento/Troca de Status no Backend
        if (this.actionType === 'Finish') {
          if (this.totalExtrusionBars <= 0 && this.totalExtrusionKg <= 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Finalização Bloqueada',
                icon: 'AlertCircleIcon',
                text: 'Não é possível finalizar a extrusão sem realizar apontamentos de quantidade (barras produzidas).',
                variant: 'danger'
              }
            });
            return;
          }

          const situationVal = this.data.situation.value;
          const reasonVal = (situationVal === 'Reprovado' || situationVal === 'Aprovado parcial')
            ? (this.data.reason?.value || this.selectedReason?.name || this.selectedReason?.id || '')
            : null;
          const obsVal = this.data.observation?.value || '';

          try {
            await finalizeExtrusion({
              operationId: this.orderObject?.operationId || orderId || 0,
              resourceId: resourceId || 0,
              situation: situationVal,
              rejectionReason: reasonVal,
              observation: obsVal,
              note: obsVal,
              eventTime: this.adjustTimeZone3(new Date())
            });
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Extrusão Finalizada',
                icon: 'CheckIcon',
                text: 'Extrusão finalizada com sucesso!',
                variant: 'success'
              }
            });
          } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao finalizar extrusão. Verifique se foram apontadas quantidades.';
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Erro ao Finalizar',
                icon: 'AlertCircleIcon',
                text: msg,
                variant: 'danger'
              }
            });
            return;
          }
        } else if (this.actionType === 'Basket' || this.actionType === 'CuttingBasket' || this.actionType === 'CuttingUnified') {
          let orderBarSize = Number(this.orderObject?.barSizeMm)
            || Number(this.orderObject?.orderParameters?.tamanho_barra_mm)
            || Number(this.orderObject?.productParameters?.tamanho_barra_mm)
            || null;
          if (orderBarSize !== null && orderBarSize > 0 && orderBarSize <= 50) {
            orderBarSize *= 1000;
          }

          const payloadBaskets = this.basketsList
            .filter(b => !!String(b.basketCode || '').trim() && Number(b.barQuantity || 0) > 0)
            .map(b => {
              let size = (b.barSizeMm && Number(b.barSizeMm) > 0) ? Number(b.barSizeMm) : orderBarSize;
              if (size !== null && size > 0 && size <= 50) {
                size *= 1000;
              }
              return {
                id: (typeof b.id === 'number' && b.id < 1000000000) ? b.id : null,
                basketCode: String(b.basketCode || '').trim(),
                barQuantity: Number(b.barQuantity || 0),
                barSizeMm: size
              };
            });

          const payloadRefuses = (Array.isArray(this.refusesList) ? this.refusesList : [])
            .filter(r => (Number(r.quantityKg) > 0 || Number(r.quantityBars) > 0) && (r.reasonId || r.refuseReasonId))
            .map(r => ({
              id: (typeof r.id === 'number' && r.id < 1000000000) ? r.id : null,
              quantityKg: Number(r.quantityKg || 0),
              quantityBars: r.quantityBars ? Number(r.quantityBars) : null,
              refuseReasonId: Number(r.reasonId || r.refuseReasonId || 1),
              observation: r.observation || ''
            }));

          await Promise.all([
            saveCuttingBaskets({
              operationId: this.orderObject?.operationId || orderId || 0,
              resourceId: resourceId || 0,
              baskets: payloadBaskets,
              eventTime: this.adjustTimeZone3(new Date())
            }),
            saveCuttingRefuses({
              operationId: this.orderObject?.operationId || orderId || 0,
              resourceId: resourceId || 0,
              refuses: payloadRefuses,
              eventTime: this.adjustTimeZone3(new Date())
            })
          ]);

          // Verificação de Conciliação e Finalização Automática da Ordem no Corte
          const totalCuttingBarsSaved = payloadBaskets.reduce((acc, b) => acc + (Number(b.barQuantity) || 0), 0)
            + payloadRefuses.reduce((acc, r) => acc + (Number(r.quantityBars) || 0), 0);

          let autoFinalized = false;
          if (this.totalExtrusionBars > 0 && totalCuttingBarsSaved === this.totalExtrusionBars) {
            try {
              await finalizeCutting({
                operationId: this.orderObject?.operationId || orderId || 0,
                resourceId: resourceId || 0,
                observation: 'Finalizado automaticamente (100% conciliado).',
                eventTime: this.adjustTimeZone3(new Date())
              });
              autoFinalized = true;
            } catch (autoErr) {
              console.warn('Aviso ao finalizar automaticamente ordem no corte:', autoErr);
            }
          }

          if (autoFinalized) {
            const detailText = payloadBaskets.length === 0
              ? `Refugos do corte salvos com sucesso! Como a quantidade de ${totalCuttingBarsSaved} barras refugadas conciliou 100% com a prensa, a ordem no corte foi finalizada automaticamente.`
              : `Apontamento salvo com sucesso! Como a quantidade de ${totalCuttingBarsSaved} barras conciliou 100% com a prensa, a ordem no corte foi finalizada automaticamente.`;
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Ordem Finalizada Automaticamente',
                icon: 'CheckIcon',
                text: detailText,
                variant: 'success'
              }
            });
          } else {
            const detailText = payloadBaskets.length === 0
              ? 'Apontamento de refugos do corte salvo com sucesso!'
              : 'Apontamento de cestos e refugos do corte salvo com sucesso!';
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Corte Salvo',
                icon: 'CheckIcon',
                text: detailText,
                variant: 'success'
              }
            });
          }
        } else if (this.actionType === 'FinishCutting') {
          if (this.totalExtrusionBars <= 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Produção Incompleta',
                icon: 'AlertCircleIcon',
                text: 'Não é possível finalizar o corte sem produção de extrusão apontada.',
                variant: 'danger'
              }
            });
            return;
          }

          if (this.currentCuttingTotalBars <= 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Produção Incompleta',
                icon: 'AlertCircleIcon',
                text: 'Não é possível finalizar o corte sem realizar apontamentos de quantidade (cestos ou refugos).',
                variant: 'danger'
              }
            });
            return;
          }

          if (this.currentCuttingTotalBars < this.totalExtrusionBars) {
            const missing = this.totalExtrusionBars - this.currentCuttingTotalBars;
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Produção Incompleta',
                icon: 'AlertCircleIcon',
                text: `Não é possível finalizar. Faltam ${missing} barra(s) no corte para conciliar a produção da prensa (${this.totalExtrusionBars} barras).`,
                variant: 'danger'
              }
            });
            return;
          }

          try {
            await finalizeCutting({
              operationId: this.orderObject?.operationId || orderId || 0,
              resourceId: resourceId || 0,
              observation: '',
              eventTime: this.adjustTimeZone3(new Date())
            });
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Ordem Finalizada',
                icon: 'CheckIcon',
                text: 'Ordem no corte finalizada e 100% conciliada com sucesso!',
                variant: 'success'
              }
            });
          } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao finalizar ordem no corte.';
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Erro ao Finalizar',
                icon: 'AlertCircleIcon',
                text: msg,
                variant: 'danger'
              }
            });
            return;
          }
        } else if (this.actionType === 'Partial') {
          await createTarugoAppointment({
            operationId: this.orderObject?.operationId || orderId || 0,
            resourceId: resourceId || 0,
            alloy: String(this.data.alloy?.value || ''),
            heatNumber: String(this.data.heatNumber?.value || ''),
            barQuantity: Number(this.data.barQuantity?.value || 0),
            quantityBars: Number(this.data.barQuantity?.value || 0),
            origin: String(this.data.origin?.value || 'Alumasa'),
            sizeMm: Number(this.data.sizeMm?.value || 0),
            quantity: Number(this.data.quantity?.value || 0),
            pullLengthM: Number(this.data.pullLengthM?.value || 0),
            temperature: Number(this.data.temperature?.value || 0),
            emergentTemperature: Number(this.data.emergentTemperature?.value || 0),
            extrusionSpeedMMin: Number(this.data.extrusionSpeed?.value || 0),
            profileWeightKgM: Number(this.data.profileWeight?.value || 0),
            eventTime: this.adjustTimeZone3(new Date())
          });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Apontamento Registrado',
              icon: 'CheckIcon',
              text: 'Apontamento de tarugo registrado com sucesso!',
              variant: 'success'
            }
          });
        } else {
          // Determine target status: use selectedStatus.id if chosen, or resolve by actionType
          let targetStatusId = this.selectedStatus?.id;
          if (!targetStatusId) {
            if (this.actionType === 'Setup') {
              targetStatusId = 2;
            } else if (this.actionType === 'Stop') {
              const stopStatusObj = Array.isArray(this.contextStatusList)
                ? this.contextStatusList.find(s =>
                    s.status_classification_id === 2 ||
                    (s.name && (s.name.toLowerCase().includes('paus') || s.name.toLowerCase().includes('parad')))
                  )
                : null;
              targetStatusId = stopStatusObj?.id || 11;
            } else {
              targetStatusId = 3;
            }
          }

          await changeExtrusionStatus({
            operationId: this.orderObject?.operationId || orderId || 0,
            resourceId: resourceId || 0,
            statusId: targetStatusId,
            refuseReasonId: null,
            note: '',
            eventTime: this.adjustTimeZone3(new Date())
          });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Apontamento Registrado',
              icon: 'CheckIcon',
              text: 'Apontamento registrado com sucesso!',
              variant: 'success'
            }
          });
        }

        // Emitir evento para o painel atualizar
        this.isOpen = false;
        this.$emit('confirm', {
          actionType: this.actionType,
          orderCode: orderCode,
          material: this.selectedMaterial,
          reason: this.selectedReason,
          produced: this.data.produced?.value,
          rejection: this.data.rejection?.value,
          date: this.datepicker,
          dynamicFields: this.dynamicFieldsList
        });
      } catch (e) {
        console.error('Erro na confirmação do apontamento:', e);
        const errMsg = e?.response?.data?.message || e?.message || 'Erro ao registrar apontamento';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Confirmar',
            icon: 'AlertTriangleIcon',
            text: errMsg,
            variant: 'danger'
          }
        });
      } finally {
        this.submitting = false;
        this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
      }
    },
    adjustTimeZone3(inputDate) {
      let date = inputDate || new Date();
      if (!(date instanceof Date)) {
        date = new Date(date);
      }
      const offset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - offset).toISOString().slice(0, -1);
    },
    handleChecklistSubmit(data) {
      this.showChecklistForm = false;
    },
    handleChecklistFinish() {
      this.showChecklistForm = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.appointment-modal-container {
  overflow: auto;
  height: 100dvh;
  width: 100vw;
  background: #fcfcfc;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;

  .fields-container {
    padding: 24px 29px calc(24px + 63px + 24px);
    display: flex;
    flex-grow: 1;

    .fields-wrapper {
      width: 100%;
      display: flex;
      flex-direction: column;
      position: relative;

      .top-op-cards-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-auto-rows: 124px;
        gap: 16px 30px;
        margin-bottom: 20px;
      }

      .fields-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-auto-rows: 124px;
        gap: 16px 30px;

        &.fields-list-3col {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          gap: 16px 20px !important;

          .grid-full {
            grid-column: span 3 !important;
          }
        }

        .grid-full {
          grid-column: span 2;
        }
      }

      .apm__button-c {
        width: 100%;
        padding: 0 24px;
        position: fixed;
        bottom: 24px;
        left: 0;

        button {
          margin: 0 auto;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .appointment-modal-container {
    .fields-container {
      padding: 16px 16px calc(16px + 52px + 16px);

      .fields-wrapper {
        .top-op-cards-grid {
          grid-template-columns: minmax(0, 1fr) !important;
          grid-auto-rows: auto !important;
          margin-bottom: 16px !important;
        }

        .fields-list {
          grid-template-columns: minmax(0, 1fr) !important;
          grid-auto-rows: auto !important;

          &.fields-list-3col {
            grid-template-columns: minmax(0, 1fr) !important;

            .grid-full {
              grid-column: inherit !important;
            }
          }

          .grid-full {
            grid-column: inherit !important;
          }
        }

        .apm__button-c {
          padding: 0 16px !important;
          bottom: 16px !important;
        }
      }
    }
  }
}

::v-deep .load-spinner-global {
  height: 50px !important;
  width: 50px !important;

  circle {
    fill: #974900 !important;
  }
}

.tarugo-fields-grid {
  width: 100%;
}

.tarugo-grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
  width: 100%;
}

.tarugo-grid-full {
  grid-column: 1 / -1;
}

.tarugo-input-card {
  background: #fafafa;
  border: 1px solid #cfc4be;
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s, background 0.2s;

  &.tarugo-card-alert {
    border-color: #ffa726 !important;
    background: #fff9f0;
  }

  &:focus-within {
    border-color: #974900 !important;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(151, 73, 0, 0.1);
  }

  .tarugo-label {
    font-family: "Raleway", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #7e7570;
    margin-bottom: 4px;
  }

  .tarugo-alert-badge {
    font-family: "Raleway", sans-serif;
    font-size: 11px;
    font-weight: 700;
    color: #e65100;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .tarugo-input,
  .tarugo-select {
    width: 100%;
    height: 38px;
    padding: 6px 12px;
    border: 1px solid #cfc4be;
    border-radius: 6px;
    background: #fff;
    color: #4c4541;
    font-size: 14.5px;
    font-weight: 600;
    outline: none;

    &:focus {
      border-color: #974900;
    }
  }
}

/* SFC Stepper & Multi-Entry Styles (MultipleRefuses Pattern) */
.count-section {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}

.count-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 16px 20px;
  border: 1px solid #cfc4be;
  border-radius: 16px;
  background: #ffffff;

  &__label {
    font-size: 16px;
    font-weight: 600;
    line-height: 26px;
    color: #4c4541;
    font-family: "Raleway", sans-serif;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__value {
    width: 48px;
    min-width: 48px;
    height: 44px;
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    line-height: 30px;
    color: #4c4541;
    border: 1px solid #cfc4be;
    border-radius: 8px;
    background: #ffffff;
    padding: 0 4px;

    &:focus {
      outline: none;
      border-color: #974900;
    }
  }

  &__button {
    width: 44px;
    height: 44px;
    border: 1px solid #974900;
    border-radius: 8px;
    background: #ffede2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    .count-stepper__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-size: 28px;
      font-weight: 400;
      line-height: 1;
      color: #974900;
      user-select: none;
    }

    &:hover:not(:disabled) {
      background: #ffdbc4;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      border-color: #cfc4be;
      background: #f3efec;

      .count-stepper__icon {
        color: #998f8a;
      }
    }
  }
}

.refuse-entry {
  background: #ffffff;
  border: 1px solid #cfc4be;
  border-radius: 6px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  &__title {
    font-family: "Raleway", sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #4c4541;
  }

  &__fields {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
  }
}

.btn-delete-entry {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ea5455;
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
    fill: #ea5455;
  }

  &:hover {
    background: #ffebeb;
  }
}

.quantity-field-card {
  display: flex;
  align-items: stretch;
  flex: 1 1 calc(33.333% - 10px);
  min-width: 220px;
  border: 1px solid #cfc4be;
  border-radius: 16px;
  background: #fafafa;
  overflow: hidden;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &--alert {
    border-color: #ffa726 !important;

    .quantity-field-card__icon {
      border-right-color: #ffa726 !important;

      svg {
        fill: #ffa726 !important;
      }
    }
  }

  &:hover,
  &:focus-within {
    background: #ffede2;
    border-color: #974900;

    .quantity-field-card__icon {
      border-right-color: #974900;

      svg {
        fill: #974900;
      }
    }

    .quantity-field-card__label {
      color: #974900;
    }

    .quantity-field-card__input {
      color: #974900;
      border-color: #974900;
    }

    .quantity-field-card__warn svg {
      fill: #974900;
    }
  }

  &__icon {
    min-width: 60px;
    max-width: 60px;
    border-right: 1px solid #cfc4be;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s ease;

    svg {
      width: 22px;
      height: 22px;
      fill: #998f8a;
      transition: fill 0.2s ease;
    }
  }

  &__content {
    flex: 1;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    color: #998f8a;
    margin-bottom: 4px;
    font-family: "Raleway", sans-serif;
    letter-spacing: 0.5px;
  }

  &__input {
    width: 100%;
    height: 38px;
    border: 1px solid #cfc4be;
    border-radius: 6px;
    background: #ffffff;
    font-weight: 600;
    font-size: 15px;
    line-height: 24px;
    color: #4c4541;
    padding: 6px 12px;
    outline: none;
    transition: all 0.2s ease;

    &:focus {
      border-color: #974900;
      box-shadow: 0 0 0 2px rgba(151, 73, 0, 0.15);
    }

    &--select {
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      padding-right: 28px;
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-image: url("data:image/svg+xml;charset=utf8,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 1.44333L0.943 0.5L4.00133 3.613L7.057 0.5L8 1.44333L4.00133 5.5L0 1.44333Z' fill='%23974900'/%3E%3C/svg%3E");
    }

    &--placeholder {
      color: #998f8a !important;
    }
  }

  &__warn {
    padding: 0 16px;
    min-width: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 20px;
      height: 20px;
      min-width: 20px;
      min-height: 20px;
      fill: #ffa726;
      flex-shrink: 0;
      display: block;
    }
  }
}

.partial-direct-inputs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  width: 100%;
  margin-bottom: 20px;

  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }

  .quantity-field-card {
    min-width: 0;
    flex: unset;
  }
}

.finish-direct-inputs-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 20px;
  width: 100%;
  margin-bottom: 20px;

  &--3col {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr !important;
  }

  .quantity-field-card {
    min-width: 0;
    flex: unset;
    min-height: 84px;
  }
}

.cutting-balance-summary {
  background: #fdfbf9;
  border: 1px solid #cfc4be;
  border-radius: 12px;
  padding: 14px 18px;
  width: 100%;
  transition: all 0.2s ease;

  &--exceeded {
    border-color: #ea5455;
    background: #fff5f5;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 18px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #79716b;
    letter-spacing: 0.5px;
  }

  &__val {
    font-size: 14px;
    font-weight: 600;
    color: #2b2523;
  }

  &__warning {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #ffd2d2;
    color: #ea5455;
    font-size: 13px;
    font-weight: 600;

    svg {
      width: 18px;
      height: 18px;
      fill: #ea5455;
      flex-shrink: 0;
    }
  }

  &__success {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #c3f2d7;
    color: #28c76f;
    font-size: 13px;
    font-weight: 600;
  }

  &--perfect {
    border-color: #28c76f;
    background: #f4fbf6;
  }
}

.cutting-section-block {
  background: #ffffff;
  border: 1px solid #e2dedc;
  border-radius: 12px;
  padding: 16px;
  width: 100%;
}

.cutting-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0eeed;
}

.cutting-section-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #2b2523;
}

.section-icon-box {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.bg-purple-light {
    background: rgba(115, 103, 240, 0.12);
  }

  &.bg-danger-light {
    background: rgba(234, 84, 85, 0.12);
  }
}

.text-purple {
  color: #7367f0;
}

.btn-add-section-item {
  display: inline-flex;
  align-items: center;
  background: #f8f6f5;
  border: 1px solid #cfc4be;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #5d5955;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #7367f0;
    border-color: #7367f0;
    color: #ffffff;
  }

  &.btn-add-refuse:hover {
    background: #ea5455;
    border-color: #ea5455;
    color: #ffffff;
  }
}

.empty-refuses-card {
  background: #faf8f7;
  border: 1px dashed #dcd4cf;
  border-radius: 10px;
}

.cutting-reconciliation-card {
  background: #ffffff;
  border: 1px solid #cfc4be;
  border-radius: 14px;
  padding: 18px;
  width: 100%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  &__title {
    font-family: "Raleway", sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: #2b2523;
  }

  &__badge {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;

    &.badge-success {
      background: #e8f8ed;
      color: #28c76f;
    }

    &.badge-warning {
      background: #fff3e0;
      color: #e65100;
    }

    &.badge-danger {
      background: #ffebee;
      color: #c62828;
    }

    &.badge-neutral {
      background: #f0eeed;
      color: #79716b;
    }
  }

  &__metrics {
    display: flex;
    align-items: center;
    justify-content: space-around;
    background: #f8f6f5;
    border-radius: 10px;
    padding: 14px 16px;
    gap: 12px;

    @media (max-width: 576px) {
      flex-direction: column;
      gap: 8px;
    }
  }

  .reconciliation-metric {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 2px;

    &__label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      color: #79716b;
    }

    &__value {
      font-size: 17px;
      font-weight: 700;
      color: #2b2523;
    }

    &__sub {
      font-size: 12px;
      color: #998f8a;
    }

    &__divider {
      font-size: 16px;
      font-weight: 700;
      color: #b3a8a2;
    }
  }

  &__alert {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border-radius: 10px;
    padding: 12px 16px;
    margin-top: 14px;

    &.alert-success {
      background: #e8f8ed;
      border: 1px solid #b7ebd0;
      color: #155724;

      .check-icon {
        background: #28c76f;
        color: #ffffff;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 13px;
        flex-shrink: 0;
      }
    }

    &.alert-warning {
      background: #fff8e6;
      border: 1px solid #ffe082;
      color: #b76e00;

      svg {
        width: 22px;
        height: 22px;
        fill: #ffa726;
        flex-shrink: 0;
      }
    }

    &.alert-danger {
      background: #fde8e8;
      border: 1px solid #f5c6cb;
      color: #721c24;

      svg {
        width: 22px;
        height: 22px;
        fill: #ea5455;
        flex-shrink: 0;
      }
    }

    &.alert-neutral {
      background: #f5f4f2;
      border: 1px solid #cfc4be;
      color: #4c4541;
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    &-title {
      font-size: 13px;
      font-weight: 700;
    }

    &-desc {
      font-size: 12px;
      line-height: 1.4;
    }
  }
}
</style>
